'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Timeline } from '@xzdarcy/react-timeline-editor';
import type { TimelineAction, TimelineEffect, TimelineRow } from '@xzdarcy/timeline-engine';
import { Pause, Play, RotateCcw, Scissors, Volume2, VolumeOff } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { useAppContext } from '@/shared/contexts/app';
import { cn } from '@/shared/lib/utils';
import { normalizeClipRange, tryBuildCloudflareVideoClipUrl } from '@/shared/lib/video-clip';
interface VideoClipEditorDialogProps {
  open: boolean;
  sourceUrl: string;
  sourceDuration?: number;
  initialStart?: number;
  initialEnd?: number;
  minDuration?: number;
  maxDuration: number;
  recommendedMinDuration?: number;
  recommendedMaxDuration?: number;
  onOpenChange: (open: boolean) => void;
  onUseClip: (clip: {
    url: string;
    preview: string;
    duration: number;
    start: number;
    end: number;
    sourceUrl: string;
  }) => void;
}
interface ClipAction extends TimelineAction {
  effectId: 'clip';
}
const DEFAULT_MIN_DURATION = 0.1;
const TIME_SCALE = 2;
const SCALE_SPLIT_COUNT = 20;
const SCALE_WIDTH = 120;
const ACTION_ID = 'reference-clip';
const effects: Record<string, TimelineEffect> = {
  clip: {
    id: 'clip',
    name: 'Reference Clip'
  }
};
const makeRows = (start: number, end: number): TimelineRow[] => [{
  id: 'reference-track',
  rowHeight: 58,
  actions: [{
    id: ACTION_ID,
    effectId: 'clip',
    start,
    end,
    selected: true,
    flexible: false,
    movable: true,
    minStart: 0
  } satisfies ClipAction]
}];
const readActionRange = (rows: TimelineRow[]) => {
  const action = rows[0]?.actions.find(item => item.id === ACTION_ID);
  return {
    start: action?.start ?? 0,
    end: action?.end ?? DEFAULT_MIN_DURATION
  };
};
export function VideoClipEditorDialog({
  open,
  sourceUrl,
  sourceDuration,
  initialStart = 0,
  initialEnd: initialEndProp,
  minDuration = DEFAULT_MIN_DURATION,
  maxDuration,
  recommendedMinDuration,
  recommendedMaxDuration,
  onOpenChange,
  onUseClip
}: VideoClipEditorDialogProps) {
  const t = useTranslations('common.uploader');
  const {
    configs,
    fetchConfigs
  } = useAppContext();
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewFrameRef = useRef<number | null>(null);
  const effectiveSourceDuration = sourceDuration ?? maxDuration;
  const maxSelectableDuration = Math.min(maxDuration, effectiveSourceDuration);
  const initialRange = normalizeClipRange({
    start: initialStart,
    end: initialEndProp ?? Math.min(effectiveSourceDuration, maxDuration),
    sourceDuration: effectiveSourceDuration,
    minDuration,
    maxDuration
  });
  const [rows, setRows] = useState<TimelineRow[]>(() => makeRows(initialRange.start, initialRange.end));
  const [previewTime, setPreviewTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPreviewMuted, setIsPreviewMuted] = useState(true);
  const [sourceWidth, setSourceWidth] = useState<number | undefined>();
  const {
    start,
    end
  } = useMemo(() => readActionRange(rows), [rows]);
  const duration = Number((end - start).toFixed(1));
  const transformOrigin = configs.r2_domain || '';
  const isInvalid = duration <= 0 || !transformOrigin;
  const isOutsideRecommendedDuration = typeof recommendedMinDuration === 'number' && duration < recommendedMinDuration || typeof recommendedMaxDuration === 'number' && duration > recommendedMaxDuration;
  const effectiveRecommendedMinDuration = recommendedMinDuration ?? minDuration;
  const effectiveRecommendedMaxDuration = recommendedMaxDuration ?? maxDuration;
  const syncPreviewProgress = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.currentTime >= end) {
      video.currentTime = start;
      video.pause();
      setIsPlaying(false);
      setPreviewTime(0);
      return;
    }
    setPreviewTime(Math.max(0, Number((video.currentTime - start).toFixed(1))));
  }, [end, start]);
  const updateRange = (nextStart: number, nextEnd: number) => {
    const normalized = normalizeClipRange({
      start: nextStart,
      end: nextEnd,
      sourceDuration: effectiveSourceDuration,
      minDuration,
      maxDuration
    });
    setRows(makeRows(normalized.start, normalized.end));
    const video = videoRef.current;
    if (video) {
      video.currentTime = normalized.start;
      video.pause();
    }
    setIsPlaying(false);
    setPreviewTime(0);
  };
  const syncRangeDuringEdit = (nextStart: number, nextEnd: number) => {
    const normalized = normalizeClipRange({
      start: nextStart,
      end: nextEnd,
      sourceDuration: effectiveSourceDuration,
      minDuration,
      maxDuration
    });
    setRows(makeRows(normalized.start, normalized.end));
    const video = videoRef.current;
    if (video) {
      video.currentTime = normalized.start;
      video.pause();
    }
    setIsPlaying(false);
    setPreviewTime(0);
  };
  useEffect(() => {
    if (!open) return;
    updateRange(initialRange.start, initialRange.end);
    // Reset only when the dialog opens or source constraints change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRange.end, initialRange.start, open, sourceUrl]);
  useEffect(() => {
    if (open && !configs.r2_domain) {
      fetchConfigs();
    }
  }, [configs.r2_domain, fetchConfigs, open]);
  useEffect(() => {
    if (!open) return;
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = start;
    setPreviewTime(0);
  }, [open, sourceUrl, start]);
  useEffect(() => {
    if (!open || !isPlaying) return;
    const tick = () => {
      syncPreviewProgress();
      previewFrameRef.current = requestAnimationFrame(tick);
    };
    previewFrameRef.current = requestAnimationFrame(tick);
    return () => {
      if (previewFrameRef.current) {
        cancelAnimationFrame(previewFrameRef.current);
        previewFrameRef.current = null;
      }
    };
  }, [isPlaying, open, syncPreviewProgress]);
  const handleDurationInput = (value: number) => {
    const nextDuration = Math.min(Math.max(value || minDuration, minDuration), maxSelectableDuration);
    updateRange(start, start + nextDuration);
  };
  const handleResetRange = () => {
    updateRange(0, Math.min(effectiveSourceDuration, maxDuration));
  };
  const startResizeFromPointer = (direction: 'left' | 'right', actionStart: number, actionEnd: number, event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const originX = event.clientX;
    const secondsPerPixel = TIME_SCALE / SCALE_WIDTH;
    const handlePointerMove = (moveEvent: PointerEvent) => {
      const deltaSeconds = (moveEvent.clientX - originX) * secondsPerPixel;
      if (direction === 'left') {
        const minStart = Math.max(0, actionEnd - maxDuration);
        const maxStart = actionEnd - minDuration;
        const nextStart = Math.min(Math.max(actionStart + deltaSeconds, minStart), maxStart);
        syncRangeDuringEdit(nextStart, actionEnd);
        return;
      }
      const minEnd = actionStart + minDuration;
      const maxEnd = Math.min(effectiveSourceDuration, actionStart + maxDuration);
      const nextEnd = Math.min(Math.max(actionEnd + deltaSeconds, minEnd), maxEnd);
      syncRangeDuringEdit(actionStart, nextEnd);
    };
    const handlePointerUp = () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  };
  const togglePreviewPlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      if (video.currentTime < start || video.currentTime >= end) {
        video.currentTime = start;
      }
      setIsPlaying(true);
      syncPreviewProgress();
      video.play().catch(() => {
        setIsPlaying(false);
      });
      return;
    }
    setIsPlaying(false);
    video.pause();
  };
  const handlePreviewScrub = (value: number) => {
    const video = videoRef.current;
    if (!video) return;
    const nextPreviewTime = Math.min(Math.max(value, 0), duration);
    video.currentTime = start + nextPreviewTime;
    setPreviewTime(Number(nextPreviewTime.toFixed(1)));
  };
  const handleUseClip = () => {
    const normalized = normalizeClipRange({
      start,
      end,
      sourceDuration: effectiveSourceDuration,
      minDuration,
      maxDuration
    });
    if (!transformOrigin) {
      return;
    }
    const {
      url
    } = tryBuildCloudflareVideoClipUrl({
      endpointOrigin: transformOrigin,
      sourceUrl,
      start: normalized.start,
      duration: normalized.duration,
      width: sourceWidth || 720
    });
    if (!url) {
      toast.error(t('clip_dialog_url_failed'));
      return;
    }
    onUseClip({
      url,
      preview: url,
      duration: normalized.duration,
      start: normalized.start,
      end: normalized.end,
      sourceUrl
    });
    onOpenChange(false);
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="text-primary h-5 w-5" />
            {t('clip_dialog_title')}
          </DialogTitle>
          <DialogDescription>{t('clip_dialog_description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted overflow-hidden rounded-lg border">
            <div className="relative">
              <video ref={videoRef} src={sourceUrl} className="max-h-[340px] w-full object-contain" muted={isPreviewMuted} playsInline onLoadedMetadata={event => {
              setSourceWidth(event.currentTarget.videoWidth || undefined);
            }} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onTimeUpdate={syncPreviewProgress} />
              <button type="button" className="absolute right-2 bottom-2 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white/90 transition-colors hover:bg-black/80" onClick={event => {
              event.stopPropagation();
              setIsPreviewMuted(prev => !prev);
            }} aria-label={isPreviewMuted ? 'Unmute' : 'Mute'}>
                {isPreviewMuted ? <VolumeOff className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
              </button>
            </div>
            <div className="border-border bg-background px-3 py-2">
              <div className="flex items-center gap-3">
                <Button type="button" size="icon" variant="secondary" className="h-8 w-8 shrink-0 rounded-full" onClick={togglePreviewPlayback} aria-label={isPlaying ? t('clip_dialog_pause_preview') : t('clip_dialog_play_preview')}>
                  {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
                <input type="range" min={0} max={duration} step={0.1} value={Math.min(previewTime, duration)} className="accent-primary h-2 w-full" onChange={event => handlePreviewScrub(Number(event.target.value))} aria-label={t('clip_dialog_preview_progress')} />
                <div className="text-muted-foreground w-28 text-right font-mono text-xs">
                  {previewTime.toFixed(1)} / {duration.toFixed(1)}s
                </div>
              </div>
            </div>
          </div>

          <div className="mb-2 grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-xs">
            <span className="text-muted-foreground">
              {start.toFixed(1)}s - {end.toFixed(1)}s
            </span>
            <div className="bg-muted flex items-center gap-2 rounded-full px-2 py-1">
              <Label htmlFor="reference-clip-duration" className="text-muted-foreground text-[10px]">
                {t('clip_dialog_duration')}
              </Label>
              <Input id="reference-clip-duration" type="number" min={0.1} max={maxSelectableDuration} step={0.1} value={duration.toFixed(1)} className="h-6 w-20 px-2 text-center text-xs" onChange={event => handleDurationInput(Number(event.target.value))} />
            </div>
            <div className="flex justify-end">
              <Button type="button" size="icon" variant="ghost" className="text-muted-foreground hover:text-foreground h-8 w-8" onClick={handleResetRange} aria-label={t('clip_dialog_reset_range')}>
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="clip-timeline-demo overflow-hidden rounded-xl border">
            <Timeline editorData={rows} effects={effects} scale={TIME_SCALE} scaleWidth={SCALE_WIDTH} scaleSplitCount={SCALE_SPLIT_COUNT} minScaleCount={Math.max(4, Math.ceil(effectiveSourceDuration / TIME_SCALE))} maxScaleCount={Math.max(4, Math.ceil(effectiveSourceDuration / TIME_SCALE))} startLeft={12} rowHeight={58} gridSnap dragLine={false} hideCursor autoScroll={false} style={{
            width: '100%',
            height: 112
          }} onChange={nextRows => {
            const nextRange = readActionRange(nextRows);
            updateRange(nextRange.start, nextRange.end);
          }} onActionMoving={({
            start: nextStart,
            end: nextEnd
          }) => {
            const isValid = nextStart >= 0 && nextEnd <= effectiveSourceDuration && nextEnd - nextStart <= maxDuration;
            if (isValid) {
              syncRangeDuringEdit(nextStart, nextEnd);
            }
            return isValid;
          }} onActionResizing={({
            start: nextStart,
            end: nextEnd
          }) => {
            const isValid = nextStart >= 0 && nextEnd <= effectiveSourceDuration && nextEnd - nextStart >= minDuration && nextEnd - nextStart <= maxDuration;
            if (isValid) {
              syncRangeDuringEdit(nextStart, nextEnd);
            }
            return isValid;
          }} getScaleRender={scale => <span className="text-muted-foreground text-[10px]">
                  {scale}s
                </span>} getActionRender={action => <div className={cn('bg-primary text-primary-foreground relative flex h-full items-center justify-center rounded-lg border px-8 text-xs font-semibold shadow-sm select-none', action.selected && 'ring-primary/30 ring-2')}>
                  <div className="absolute inset-y-0 left-0 z-30 w-8 cursor-ew-resize" onPointerDown={event => startResizeFromPointer('left', action.start, action.end, event)} aria-label="Adjust clip start" role="button" tabIndex={0}>
                    <span className="bg-chart-4 absolute inset-y-0 left-0 w-1 rounded-full" />
                  </div>
                  {t('clip_dialog_action_label')}
                  <div className="absolute inset-y-0 right-0 z-30 w-8 cursor-ew-resize" onPointerDown={event => startResizeFromPointer('right', action.start, action.end, event)} aria-label="Adjust clip end" role="button" tabIndex={0}>
                    <span className="bg-chart-4 absolute inset-y-0 right-0 w-1 rounded-full" />
                  </div>
                </div>} />
          </div>

          {isInvalid && <p className="text-destructive text-sm">
              {t('clip_dialog_invalid_duration', {
            min: 0.1,
            max: maxDuration
          })}
            </p>}

          {!isInvalid && isOutsideRecommendedDuration && <p className="text-primary text-sm">
              {effectiveRecommendedMinDuration === effectiveRecommendedMaxDuration ? t('clip_dialog_required_duration_fixed', {
            seconds: effectiveRecommendedMinDuration
          }) : t('clip_dialog_required_duration', {
            min: effectiveRecommendedMinDuration,
            max: effectiveRecommendedMaxDuration
          })}
            </p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('clip_dialog_cancel')}
          </Button>
          <Button onClick={handleUseClip} disabled={isInvalid || !sourceUrl}>
            {t('clip_dialog_use_clip')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}